import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { BarList, Drawer, DrawerSection, KeyValues, Segmented } from '../components/data';
import { Gauge } from '../components/Gauge';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, pct, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

type Kind = 'ticket' | 'text' | 'number' | 'hours' | 'money' | 'percent' | 'date' | 'datetime';
type Format = 'percent' | 'number' | 'hours' | 'duration' | 'money' | 'ratio' | 'rating';
type Group = 'Service desk' | 'People & time' | 'Money & contracts' | 'Clients';

/* A drill column. A `t.` key is read from the ticket the row's ticketId names. */
interface Column {
  key: string;
  label: string;
  kind?: Kind;
}
interface Metric {
  key: string;
  group: Group;
  title: string;
  question: string;
  visual: 'gauge' | 'stat' | 'bars';
  value: number | null;
  format: Format;
  foot: string;
  flagged?: boolean;
  gauges?: { label: string; value: number | null; target: number | null; max: number; unit: string; decimals: number }[];
  bars?: { name: string; sub?: string; value: number; display: string }[];
  source: { entities: string; filter: string; fields: string; formula: string; window: string; records: number; note?: string };
  drill: { columns: Column[]; rows: Record<string, unknown>[]; note?: string };
}
interface TicketInfo {
  number: string;
  title: string | null;
  link: string | null;
  company: string | null;
  status: string | null;
  priority: string | null;
  queue: string | null;
  issue: string | null;
  subIssue: string | null;
  source: string | null;
  assigned: string | null;
  created: string | null;
  completed: string | null;
}
interface Payload {
  from: string;
  to: string;
  tz: string;
  metrics: Metric[];
  tickets: Record<string, TicketInfo>;
  fetchedUtc: string;
}

const GROUPS: Group[] = ['Service desk', 'People & time', 'Money & contracts', 'Clients'];
/* Rows drawn at a time in a drill — an open backlog can run to thousands. */
const PAGE = 200;

const day = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—');
const stamp = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString([], { month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }) : '—';

/** A metric's headline in its own unit. */
function figure(v: number | null, f: Format): string {
  if (v === null) return '—';
  switch (f) {
    case 'percent':
      return pct(v, 1);
    case 'hours':
      return `${num(v, 1)} h`;
    case 'duration':
      return v < 48 ? `${num(v, 1)} hours` : `${num(v / 24, 1)} days`;
    case 'money':
      return formatNumber(v, 'currency');
    case 'ratio':
      return num(v, 2);
    case 'rating':
      return `${num(v, 2)} / 5`;
    default:
      return num(v);
  }
}

function quick(k: 'month' | 'lastMonth' | 'last90' | 'ytd'): { from: string; to: string } {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (k === 'month') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
  if (k === 'last90') {
    const s = new Date(d);
    s.setDate(s.getDate() - 89);
    return { from: toLocalDateString(s), to: todayLocal() };
  }
  if (k === 'ytd') return { from: toLocalDateString(new Date(d.getFullYear(), 0, 1)), to: todayLocal() };
  return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0)) };
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'last90', label: 'Last 90 days' },
  { k: 'ytd', label: 'Year to date' },
];

/** One drill cell's value — from the row, or from its ticket for a `t.` key. */
function cellValue(row: Record<string, unknown>, col: Column, tickets: Payload['tickets']): unknown {
  if (!col.key.startsWith('t.')) return row[col.key];
  const t = tickets[String(row.ticketId)];
  return t ? t[col.key.slice(2) as keyof TicketInfo] : null;
}

/** The same cell as plain text, for search and the CSV. */
function cellText(row: Record<string, unknown>, col: Column, tickets: Payload['tickets']): string {
  const v = cellValue(row, col, tickets);
  if (v === null || v === undefined || v === '') return '';
  if (col.kind === 'ticket') {
    const t = tickets[String(v)];
    return t ? `${t.number}${t.title ? ` ${t.title}` : ''}` : String(v);
  }
  return String(v);
}

function Cell({ row, col, tickets }: { row: Record<string, unknown>; col: Column; tickets: Payload['tickets'] }): ReactNode {
  const v = cellValue(row, col, tickets);
  if (v === null || v === undefined || v === '') return <span className="subtle">—</span>;
  switch (col.kind) {
    case 'ticket': {
      const t = tickets[String(v)];
      if (!t) return <span className="mono xs">{String(v)}</span>;
      return (
        <>
          <div className="xs">
            {t.link ? (
              <a href={t.link} target="_blank" rel="noreferrer">
                {t.number}
              </a>
            ) : (
              t.number
            )}
          </div>
          {t.title && <div className="msp-cell-title">{t.title}</div>}
        </>
      );
    }
    case 'hours':
      return num(Number(v), 2);
    case 'money':
      return formatNumber(Number(v), 'currency');
    case 'percent':
      return pct(Number(v), 1);
    case 'number':
      return formatNumber(Number(v));
    case 'date':
      return day(String(v));
    case 'datetime':
      return stamp(String(v));
    default:
      return String(v);
  }
}

const numeric = (k?: Kind) => k === 'hours' || k === 'money' || k === 'percent' || k === 'number';

/** Where a figure came from, as label and value pairs — on the card and in the drill. */
function SourceList({ m }: { m: Metric }) {
  return (
    <KeyValues
      items={[
        ['Autotask', <span className="mono">{m.source.entities}</span>],
        ['Filter', m.source.filter],
        ['Fields', <span className="mono xs">{m.source.fields}</span>],
        ['Formula', m.source.formula],
        ['Window', m.source.window],
        ['Records', num(m.source.records)],
        ...(m.source.note ? ([['Note', m.source.note]] as [string, ReactNode][]) : []),
      ]}
    />
  );
}

function MetricCard({ n, m, onOpen }: { n: number; m: Metric; onOpen: () => void }) {
  const records = m.drill.rows.length;
  return (
    <section className={`card msp-card${m.gauges && m.gauges.length > 1 ? ' wide' : ''}`}>
      <header className="msp-card-head">
        <span className="msp-n" aria-hidden="true">
          {n}
        </span>
        <div>
          <h2>{m.title}</h2>
          <p className="sub">{m.question}</p>
        </div>
      </header>
      <div className="msp-card-body">
        {m.visual === 'gauge' && m.gauges ? (
          <div className="msp-gauges">
            {m.gauges.map((g) => (
              <Gauge key={g.label} label={g.label} value={g.value} target={g.target} max={g.max} unit={g.unit} decimals={g.decimals} />
            ))}
          </div>
        ) : (
          <div className={`msp-figure${m.flagged ? ' accent' : ''}`}>{figure(m.value, m.format)}</div>
        )}
        {m.visual === 'bars' && m.bars && (m.bars.length ? <BarList rows={m.bars} /> : <p className="sub">Nothing in these days.</p>)}
        <p className={`xs mt-2${m.flagged ? ' msp-flag' : ' muted'}`}>
          {m.flagged && (
            <span className="ui-glyph" aria-hidden="true">
              !{' '}
            </span>
          )}
          {m.foot}
        </p>
      </div>
      <footer className="msp-card-foot">
        <details className="msp-source">
          <summary>
            <span className="msp-source-label">Source</span> {m.source.entities} · {m.source.window}
          </summary>
          <SourceList m={m} />
        </details>
        <button type="button" className="btn btn-ghost btn-sm" disabled={!records} onClick={onOpen}>
          {records ? `See the ${num(records)} records →` : 'No records'}
        </button>
      </footer>
    </section>
  );
}

/** The records behind one metric: its source, then every row, searchable and exportable. */
function Drill({ m, d, onClose }: { m: Metric; d: Payload; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(PAGE);
  const q = search.trim().toLowerCase();
  const rows = useMemo(
    () => (q ? m.drill.rows.filter((r) => m.drill.columns.some((c) => cellText(r, c, d.tickets).toLowerCase().includes(q))) : m.drill.rows),
    [m, d, q],
  );
  const exportCsv = () =>
    downloadCsv(`msp_${m.key}_${d.from}_to_${d.to}_${fileStamp()}.csv`, [
      m.drill.columns.map((c) => c.label),
      ...rows.map((r) => m.drill.columns.map((c) => cellText(r, c, d.tickets))),
    ]);

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={m.title}
      subtitle={`${m.source.window} · ${figure(m.value, m.format)} · ${m.foot}`}
      footer={
        <div className="row-between wrap">
          <span className="xs muted">
            {num(Math.min(shown, rows.length))} of {num(rows.length)} shown
          </span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      }
    >
      <div className="msp-drill">
        <DrawerSection title="Where it comes from">
          <SourceList m={m} />
        </DrawerSection>
        <DrawerSection title="Records" count={num(m.drill.rows.length)}>
          <div className="row wrap mb-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Search">
              <input
                className="input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShown(PAGE);
                }}
                placeholder="Ticket, account, technician"
              />
            </Field>
            {m.drill.note && <p className="xs muted">{m.drill.note}</p>}
          </div>
          {rows.length === 0 ? (
            <p className="sub">Nothing matches.</p>
          ) : (
            <div className="table-wrap">
              <table className="table msp-drill-table">
                <thead>
                  <tr>
                    {m.drill.columns.map((c) => (
                      <th key={c.key} className={numeric(c.kind) ? 'num' : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, shown).map((r, i) => (
                    <tr key={i}>
                      {m.drill.columns.map((c) => (
                        <td key={c.key} className={numeric(c.kind) ? 'num' : c.kind === 'ticket' ? undefined : 'xs'}>
                          <Cell row={r} col={c} tickets={d.tickets} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length > shown && (
            <button type="button" className="btn btn-ghost btn-sm mt-2" onClick={() => setShown((s) => s + PAGE)}>
              Show {num(Math.min(PAGE, rows.length - shown))} more
            </button>
          )}
        </DrawerSection>
      </div>
    </Drawer>
  );
}

/**
 * Reports › MSP Top 20 — the twenty figures an MSP reports on, for the days
 * chosen, read live from Autotask (routes/reportsMspMetrics.ts): service desk,
 * people and time, money and contracts, and clients.
 *
 * Each card says where its number came from — the Autotask entities, filter,
 * fields, formula and window, under Source — and opens the records behind it
 * in a drawer, ticket numbers linked to Autotask. Read once per range and
 * kept until Refresh.
 */
export function MspMetricsReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('lastMonth'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [group, setGroup] = useState<'all' | Group>('all');
  const [openKey, setOpenKey] = useState<string | null>(null);
  /* Stable, so the drawer does not re-take focus on every render. */
  const close = useCallback(() => setOpenKey(null), []);
  /* The browser's zone, for deciding which tickets came in after hours. */
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);
  const data = useHeldResource<Payload>(`/reports/msp-metrics?${new URLSearchParams({ from, to, tz })}`);
  const d = data.data;

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  /* Numbered in the page's order, so "number 14" means the same card whichever group is showing. */
  const numbered = (d?.metrics ?? []).map((m, i) => ({ n: i + 1, m }));
  const shown = numbered.filter(({ m }) => group === 'all' || m.group === group);
  const opened = d?.metrics.find((m) => m.key === openKey) ?? null;

  const exportSummary = () =>
    d &&
    downloadCsv(`msp_top20_${d.from}_to_${d.to}_${fileStamp()}.csv`, [
      ['#', 'Group', 'Metric', 'Value', 'Detail', 'Autotask', 'Filter', 'Fields', 'Formula', 'Window', 'Records', 'Note'],
      ...numbered.map(({ n, m }) => [
        n, m.group, m.title, figure(m.value, m.format), m.foot, m.source.entities, m.source.filter, m.source.fields, m.source.formula,
        m.source.window, m.source.records, m.source.note ?? '',
      ]),
    ]);

  return (
    <>
      <PageHead
        title="MSP Top 20"
        lede="The twenty figures an MSP reports on — SLA, speed, backlog, utilization, revenue, contracts and client load — read live from Autotask. Every card says where its number came from and opens the records behind it."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="From">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((k) => (
                <button key={k.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(k.k))}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Show">
              <Segmented
                label="Show"
                value={group}
                onChange={setGroup}
                options={[{ id: 'all' as const, label: 'All 20' }, ...GROUPS.map((g) => ({ id: g, label: g }))]}
              />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!d} onClick={exportSummary}>
              Export summary CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            {day(from)} – {day(to)}, Autotask&rsquo;s days. Backlog, aging, workload and endpoints are as of now; contracts look 90 days ahead.
            Revenue is what Autotask has posted, so a whole, closed month reads truest. Targets on the dials are common MSP starting points, named
            under each card&rsquo;s Source.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading tickets, time, billing and contracts from Autotask" />}

      {d &&
        GROUPS.filter((g) => shown.some(({ m }) => m.group === g)).map((g) => (
          <div key={g} className="mb-4">
            <h2 className="msp-group">{g}</h2>
            <div className="msp-grid">
              {shown
                .filter(({ m }) => m.group === g)
                .map(({ n, m }) => (
                  <MetricCard key={m.key} n={n} m={m} onOpen={() => setOpenKey(m.key)} />
                ))}
            </div>
          </div>
        ))}

      {d && opened && <Drill key={opened.key} m={opened} d={d} onClose={close} />}
    </>
  );
}
